import { ColocaSurface } from "@/shared/components/layout/ColocaSurface";
import { useTranslations } from "@/shared/lang/useTranslations";
import { cn } from "@/shared/lib/utils";

import { AccessApiKeyActions } from "./AccessApiKeyActions";
import { AccessApiKeyIdentity } from "./AccessApiKeyIdentity";
import { type AccessApiKeysListProps, canOpenKeyRolesFor } from "./accessApiKeysList.types";
import { AccessCardField } from "./AccessCardField";
import { AccessEffectiveCell } from "./AccessEffectiveCell";
import { PrincipalRolesCell } from "./PrincipalRolesCell";

/**
 * Mobile face of the API keys list, rendered below `lg`. Desktop is `AccessApiKeysTable`; both take
 * `AccessApiKeysListProps`.
 *
 * Same defect as the users list: the table's grid is three fixed tracks wide inside an `overflow-hidden`
 * wrapper, so on a phone "Effective access" and both row actions were clipped out of existence — including
 * the lifecycle menu, the only way to disable a key.
 */
export function AccessApiKeysCards({
  keys,
  rolesByKey,
  rolesUnavailable,
  summaryById,
  catalogUnavailable,
  canManageRoles,
  canUpdate,
  lastUsedLabel,
  onOpenRoles,
  onEditIps,
  onDisable,
  onEnable,
}: AccessApiKeysListProps) {
  const { t } = useTranslations("settings");

  if (keys.length === 0) {
    return (
      <div className="px-3.5 py-8 text-center text-[13px] text-muted-foreground">
        {t("access_no_results")}
      </div>
    );
  }

  return (
    /* No outer frame: the host section already draws the card and its padding, and each ColocaSurface
       below draws its own — nesting them doubles the hairline. */
    <div className="flex flex-col gap-3">
      {keys.map((k) => {
        const assigned = rolesByKey[k.id] ?? [];
        const rolesFailed = rolesUnavailable.has(k.id);
        const openRolesDrawer = canOpenKeyRolesFor(k.id, catalogUnavailable, rolesUnavailable)
          ? () => onOpenRoles(k)
          : undefined;
        const hasActions = !!openRolesDrawer || canUpdate;

        return (
          <ColocaSurface
            key={k.id}
            padding="md"
            className={cn("flex flex-col gap-3", !k.is_active && "opacity-55")}
          >
            <AccessApiKeyIdentity apiKey={k} lastUsedLabel={lastUsedLabel(k)} />

            <AccessCardField label={t("access_col_roles")}>
              <PrincipalRolesCell roles={assigned} rolesFailed={rolesFailed} onShowAll={openRolesDrawer} />
            </AccessCardField>

            <AccessCardField label={t("access_col_effective")}>
              {/* `explainUnknown`: the table hides the reason in a `title`, which a phone cannot reach. */}
              <AccessEffectiveCell
                summary={summaryById[k.id]}
                noRoles={!rolesFailed && assigned.length === 0}
                rolesFailed={rolesFailed}
                catalogUnavailable={catalogUnavailable}
                explainUnknown
              />
            </AccessCardField>

            {hasActions && (
              <div className="border-t border-dashed border-border pt-2.5">
                <AccessApiKeyActions
                  apiKey={k}
                  onOpenRoles={openRolesDrawer}
                  canManageRoles={canManageRoles}
                  canUpdate={canUpdate}
                  onEditIps={onEditIps}
                  onDisable={onDisable}
                  onEnable={onEnable}
                  spread
                />
              </div>
            )}
          </ColocaSurface>
        );
      })}
    </div>
  );
}
